import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { AiApplied } from "./components/AiReview";
import { BottomNav } from "./components/BottomNav";
import { CaptureButton } from "./components/Capture";
import { TaskSheetHost } from "./components/TaskSheet";
import { AppointmentSheetHost } from "./components/AppointmentSheet";
import { RoutineSheetHost } from "./components/RoutineSheet";
import { DayTypeSheetHost } from "./components/DayTypeSheet";
import { navigate, usePath } from "./lib/hooks";
import { cachedSession, logout, verifySession, type SessionState } from "./lib/session";
import { sync } from "./lib/sync";
import { InboxScreen } from "./screens/Inbox";
import { LoginScreen } from "./screens/Login";
import { MoreScreen } from "./screens/More";
import { ShareScreen } from "./screens/Share";
import { TodayScreen } from "./screens/Today";
import { WeekScreen } from "./screens/Week";

// Vandaag, Week, Inbox and Meer come with the app; screens further away load when opened (the service worker
// keeps them for offline use too).
const RhythmScreen = lazy(() => import("./screens/Rhythm").then((m) => ({ default: m.RhythmScreen })));
const TemplateScreen = lazy(() => import("./screens/Rhythm").then((m) => ({ default: m.TemplateScreen })));
const EveningScreen = lazy(() => import("./screens/Evening").then((m) => ({ default: m.EveningScreen })));
const ChecklistScreen = lazy(() => import("./screens/Checklist").then((m) => ({ default: m.ChecklistScreen })));
const MorningScreen = lazy(() => import("./screens/Morning").then((m) => ({ default: m.MorningScreen })));
const ShoppingScreen = lazy(() => import("./screens/Shopping").then((m) => ({ default: m.ShoppingScreen })));
const MenuScreen = lazy(() => import("./screens/Menu").then((m) => ({ default: m.MenuScreen })));
const RecipesScreen = lazy(() => import("./screens/Recipes").then((m) => ({ default: m.RecipesScreen })));
const RecipeScreen = lazy(() => import("./screens/Recipes").then((m) => ({ default: m.RecipeScreen })));
const AgendaScreen = lazy(() => import("./screens/Agenda").then((m) => ({ default: m.AgendaScreen })));
const NotesScreen = lazy(() => import("./screens/Notes").then((m) => ({ default: m.NotesScreen })));
const ReviewsScreen = lazy(() => import("./screens/Reviews").then((m) => ({ default: m.ReviewsScreen })));
const CategoriesScreen = lazy(() => import("./screens/Categories").then((m) => ({ default: m.CategoriesScreen })));
const SettingsScreen = lazy(() => import("./screens/Settings").then((m) => ({ default: m.SettingsScreen })));
const AccountScreen = lazy(() => import("./screens/Account").then((m) => ({ default: m.AccountScreen })));
const NotificationsScreen = lazy(() => import("./screens/MoreSub").then((m) => ({ default: m.NotificationsScreen })));
const RoutinesScreen = lazy(() => import("./screens/Routines").then((m) => ({ default: m.RoutinesScreen })));
const DatesScreen = lazy(() => import("./screens/Dates").then((m) => ({ default: m.DatesScreen })));
const TogetherScreen = lazy(() => import("./screens/Together").then((m) => ({ default: m.TogetherScreen })));
const OccasionalScreen = lazy(() => import("./screens/Routines").then((m) => ({ default: m.OccasionalScreen })));

export function App() {
  const [session, setSession] = useState<SessionState>({ kind: "loading" });
  const path = usePath();

  const refresh = useCallback(() => {
    let alive = true;
    void (async () => {
      const cached = await cachedSession();
      if (alive && cached.kind === "user") setSession(cached);
      try {
        const checked = await verifySession();
        // null: offline or server unavailable. Keep the cached user, or show the login screen if there is none.
        if (alive) setSession(checked ?? cached);
      } catch {
        if (alive) setSession(cached);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(refresh, [refresh]);

  // A change the server refused, with a reason PK can act on (review 5c); others stay quiet as before.
  useEffect(() => {
    const reasons: Record<string, string> = {
      morning_clash: "Die ochtend past niet: een stap zou tegelijk met een ander blok beginnen. De wijziging is teruggezet.",
      morning_list: "De ochtend pas je aan in Meer, Ochtend.",
      week_complete: "Je weekdoel voor de gym is al vol. Kies een gymdag om te ruilen.",
      swap_not_found: "Die gymdag kan niet meer geruild worden. Kies opnieuw.",
    };
    sync.onRefused = (reason) => {
      if (reasons[reason]) toast(reasons[reason]);
    };
    return () => {
      sync.onRefused = null;
    };
  }, []);

  useEffect(() => {
    if (session.kind !== "user") return;
    void sync.start(() => setSession({ kind: "anonymous" }));
    return () => sync.stop();
  }, [session.kind]);

  async function handleLogout() {
    sync.stop();
    await logout();
    setSession({ kind: "anonymous" });
    navigate("/");
  }

  const member = session.kind === "user" && session.me.user.role === "member";

  return (
    <>
      {session.kind === "loading" && <div aria-busy className="min-h-dvh" />}
      {session.kind === "anonymous" && <LoginScreen onLoggedIn={refresh} />}
      {session.kind === "user" && (
        <>
          <Suspense fallback={<div aria-busy className="min-h-dvh" />}>
            {member &&
            ![
              "/boodschappen",
              "/menu",
              "/recepten",
              "/agenda",
              "/meer",
              "/meer/notities",
              "/meer/meldingen",
              "/meer/toestellen",
              "/meer/afspraken",
              "/samen",
            ].includes(path) &&
            !path.startsWith("/recepten/") ? (
              // Tsjits has no planner (L113): whatever else she opens shows her shopping list.
              <ShoppingScreen />
            ) : path === "/avond" || path === "/avond/snel" ? (
              <EveningScreen key={path} me={session.me} quick={path === "/avond/snel"} />
            ) : path === "/meer/klaarleggen" ? (
              <ChecklistScreen />
            ) : path === "/meer/ochtend" ? (
              <MorningScreen key="morning" part="morning" />
            ) : path === "/boodschappen" || (member && path === "/") ? (
              <ShoppingScreen />
            ) : path === "/menu" ? (
              <MenuScreen />
            ) : path === "/recepten" ? (
              <RecipesScreen />
            ) : path.startsWith("/recepten/") ? (
              <RecipeScreen key={path} id={path.slice("/recepten/".length)} />
            ) : path === "/agenda" || path === "/meer/afspraken" ? (
              // Afspraken is one with the Agenda now; the old address still opens it.
              <AgendaScreen />
            ) : path === "/meer/notities" ? (
              <NotesScreen />
            ) : path === "/samen" ? (
              <TogetherScreen />
            ) : member && path !== "/meer" && !path.startsWith("/meer/") ? (
              // Tsjits has no planner: whatever else she opens shows her shopping list.
              <ShoppingScreen />
            ) : path === "/meer/verjaardagen" ? (
              <DatesScreen />
            ) : path === "/meer/terugblik" ? (
              <ReviewsScreen />
            ) : path === "/meer/categorieen" ? (
              <CategoriesScreen />
            ) : path === "/meer/avond" ? (
              <MorningScreen key="evening" part="evening" />
            ) : path === "/meer/instellingen" ? (
              <SettingsScreen />
            ) : path === "/week" ? (
              <WeekScreen me={session.me} />
            ) : /^\/dag\/\d{4}-\d{2}-\d{2}$/.test(path) ? (
              <TodayScreen key={path} me={session.me} date={path.slice(5)} />
            ) : path === "/meer/ritme" ? (
              <RhythmScreen />
            ) : path.startsWith("/meer/ritme/") ? (
              <TemplateScreen key={path} id={path.slice("/meer/ritme/".length)} />
            ) : path === "/meer/routines" ? (
              <RoutinesScreen />
            ) : path === "/meer/af-en-toe" ? (
              <OccasionalScreen />
            ) : path === "/meer/meldingen" ? (
              <NotificationsScreen />
            ) : path === "/meer/toestellen" ? (
              <AccountScreen me={session.me} onLogout={handleLogout} />
            ) : path === "/meer" ? (
              <MoreScreen me={session.me} />
            ) : path === "/taken" || path === "/inbox" ? (
              <InboxScreen />
            ) : path === "/delen" ? (
              <ShareScreen />
            ) : (
              <TodayScreen me={session.me} />
            )}
          </Suspense>
          {!member && <CaptureButton />}
          <TaskSheetHost dayStart={session.me.settings?.day_starts_at ?? "04:00"} />
          <AppointmentSheetHost />
          <RoutineSheetHost />
          <DayTypeSheetHost />
          <AiApplied />
          <BottomNav role={session.me.user.role} />
        </>
      )}
      <Toaster
        position="bottom-center"
        offset={{ bottom: 152 }}
        mobileOffset={{ bottom: 152 }}
        toastOptions={{ className: "!text-base" }}
      />
    </>
  );
}
